import { useState, useEffect, useCallback } from 'react';
import {
  getChangeOrders,
  createChangeOrder,
  updateChangeOrder,
  deleteChangeOrder,
  sendChangeOrder,
  approveChangeOrder,
  declineChangeOrder,
  type ChangeOrder,
  type ChangeOrderItem,
} from '@/lib/api/changeOrders';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ToastContainer } from '@/components/common/Toast';
import { useToast } from '@/lib/hooks/useToast';

interface ChangeOrdersTabProps {
  jobId: number | string;
}

const money = (n: number) =>
  `$${(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const statusBadge: Record<ChangeOrder['status'], string> = {
  draft: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
};

// ── Line item row ─────────────────────────────────────────────────────────────

interface ItemRowProps {
  item: ChangeOrderItem;
  index: number;
  onChange: (index: number, field: keyof ChangeOrderItem, value: string) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
}

const ItemRow = ({ item, index, onChange, onRemove, disabled }: ItemRowProps) => {
  const rowTotal = (item.quantity || 0) * (item.unit_price || 0);
  return (
    <tr className="border-b border-gray-100 last:border-0">
      <td className="py-2 pr-2 w-full">
        <input
          type="text"
          value={item.description}
          onChange={e => onChange(index, 'description', e.target.value)}
          disabled={disabled}
          placeholder="Description"
          className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
        />
      </td>
      <td className="py-2 pr-2 w-24">
        <input
          type="number"
          min="0"
          step="any"
          value={item.quantity}
          onChange={e => onChange(index, 'quantity', e.target.value)}
          disabled={disabled}
          placeholder="Qty"
          className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60 text-right"
        />
      </td>
      <td className="py-2 pr-2 w-28">
        <input
          type="number"
          min="0"
          step="any"
          value={item.unit_price}
          onChange={e => onChange(index, 'unit_price', e.target.value)}
          disabled={disabled}
          placeholder="Unit price"
          className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60 text-right"
        />
      </td>
      <td className="py-2 pr-2 w-28 text-right text-sm font-medium text-gray-800 whitespace-nowrap">
        {money(rowTotal)}
      </td>
      <td className="py-2 w-8">
        <button
          type="button"
          onClick={() => onRemove(index)}
          disabled={disabled}
          className="p-1 text-gray-300 hover:text-red-500 transition-colors disabled:opacity-40"
        >
          <span className="material-symbols-outlined text-lg leading-none">remove_circle</span>
        </button>
      </td>
    </tr>
  );
};

// ── CO Form ───────────────────────────────────────────────────────────────────

interface EmptyItem extends ChangeOrderItem {
  id?: undefined;
}

const emptyItem = (): EmptyItem => ({ description: '', quantity: 1, unit_price: 0 });

interface COFormProps {
  initial?: ChangeOrder;
  jobId: number;
  onSaved: () => void;
  onCancel: () => void;
  onFormError: (msg: string) => void;
}

const COForm = ({ initial, jobId, onSaved, onCancel, onFormError }: COFormProps) => {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [taxRate, setTaxRate] = useState(initial?.tax_rate ?? 5);
  const [items, setItems] = useState<ChangeOrderItem[]>(
    initial?.items && initial.items.length > 0 ? initial.items : [emptyItem()]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const subtotal = items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0);
  const taxAmount = subtotal * ((taxRate || 0) / 100);
  const total = subtotal + taxAmount;

  const handleItemChange = (index: number, field: keyof ChangeOrderItem, value: string) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      if (field === 'quantity' || field === 'unit_price') {
        return { ...item, [field]: parseFloat(value) || 0 };
      }
      return { ...item, [field]: value };
    }));
  };

  const handleItemRemove = (index: number) => {
    setItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: title.trim(),
        description: description || undefined,
        tax_rate: taxRate,
        items: items.filter(it => it.description.trim()),
      };
      if (initial) {
        await updateChangeOrder(initial.id, payload);
      } else {
        await createChangeOrder(jobId, payload);
      }
      onSaved();
    } catch {
      const msg = 'Failed to save change order.';
      setError(msg);
      onFormError(msg);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <h3 className="text-sm font-bold text-gray-900">
        {initial ? 'Edit Change Order' : 'New Change Order'}
      </h3>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          disabled={saving}
          placeholder="Change order title"
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          disabled={saving}
          rows={3}
          placeholder="Describe the scope of changes…"
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60 resize-none"
        />
      </div>

      {/* Line items */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-2">Line Items</label>
        <div className="border border-gray-100 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead>
              <tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
                <th className="text-left px-2 py-2 font-semibold">Description</th>
                <th className="text-right px-2 py-2 font-semibold w-24">Qty</th>
                <th className="text-right px-2 py-2 font-semibold w-28">Unit Price</th>
                <th className="text-right px-2 py-2 font-semibold w-28">Total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="px-2">
              {items.map((item, i) => (
                <ItemRow
                  key={i}
                  item={item}
                  index={i}
                  onChange={handleItemChange}
                  onRemove={handleItemRemove}
                  disabled={saving}
                />
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={() => setItems(prev => [...prev, emptyItem()])}
          disabled={saving}
          className="mt-2 flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium disabled:opacity-50 transition-colors"
        >
          <span className="material-symbols-outlined text-base leading-none">add</span>
          Add line item
        </button>
      </div>

      {/* Tax rate + totals */}
      <div className="flex flex-col sm:flex-row sm:justify-end gap-4">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-600 whitespace-nowrap">Tax Rate (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={taxRate}
            onChange={e => setTaxRate(parseFloat(e.target.value) || 0)}
            disabled={saving}
            className="w-20 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60 text-right"
          />
        </div>
        <div className="bg-gray-50 rounded-lg p-3 space-y-1 min-w-[180px]">
          <div className="flex justify-between text-xs text-gray-500">
            <span>Subtotal</span>
            <span>{money(subtotal)}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Tax ({taxRate}%)</span>
            <span>{money(taxAmount)}</span>
          </div>
          <div className="flex justify-between text-sm font-bold text-gray-900 border-t border-gray-200 pt-1 mt-1">
            <span>Total</span>
            <span>{money(total)}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition-colors"
        >
          {saving ? 'Saving…' : 'Save as Draft'}
        </button>
      </div>
    </form>
  );
};

// ── Confirm state type ────────────────────────────────────────────────────────

type ConfirmAction =
  | { type: 'delete'; id: number }
  | { type: 'approve'; id: number; coNumber: string }
  | { type: 'decline'; id: number; coNumber: string };

// ── Main tab ──────────────────────────────────────────────────────────────────

export const ChangeOrdersTab = ({ jobId }: ChangeOrdersTabProps) => {
  const numericJobId = typeof jobId === 'string' ? parseInt(jobId, 10) : jobId;

  const [orders, setOrders] = useState<ChangeOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [sentUrl, setSentUrl] = useState<{ id: number; url: string } | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const { toast, toasts, removeToast } = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setOrders(await getChangeOrders(numericJobId));
    } catch {
      setError('Failed to load change orders.');
    } finally {
      setLoading(false);
    }
  }, [numericJobId]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = async () => {
    setShowNewForm(false);
    setEditingId(null);
    await load();
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    setActionInProgress(true);
    try {
      if (confirmAction.type === 'delete') {
        await deleteChangeOrder(confirmAction.id);
        toast.success('Change order deleted.');
      } else if (confirmAction.type === 'approve') {
        await approveChangeOrder(confirmAction.id);
        toast.success(`Change order ${confirmAction.coNumber} approved.`);
      } else if (confirmAction.type === 'decline') {
        await declineChangeOrder(confirmAction.id);
        toast.success(`Change order ${confirmAction.coNumber} declined.`);
      }
      setConfirmAction(null);
      await load();
    } catch {
      const label =
        confirmAction.type === 'delete' ? 'delete' :
        confirmAction.type === 'approve' ? 'approve' : 'decline';
      toast.error(`Failed to ${label} change order.`);
      setError(`Failed to ${label} change order.`);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSend = async (id: number) => {
    setSendingId(id);
    setError('');
    try {
      const { approveUrl } = await sendChangeOrder(id);
      setSentUrl({ id, url: approveUrl });
      await navigator.clipboard.writeText(approveUrl);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 3000);
      toast.success('Change order sent. Approval link copied to clipboard.');
      await load();
    } catch {
      toast.error('Failed to send change order.');
      setError('Failed to send change order.');
    } finally {
      setSendingId(null);
    }
  };

  const copyUrl = async (url: string, id: number) => {
    await navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 3000);
  };

  // Build confirm dialog props based on pending action
  const confirmProps = confirmAction ? (() => {
    if (confirmAction.type === 'delete') {
      return {
        title: 'Delete Change Order',
        message: 'Are you sure you want to delete this change order? This cannot be undone.',
        confirmLabel: 'Delete',
        variant: 'danger' as const,
      };
    } else if (confirmAction.type === 'approve') {
      return {
        title: 'Approve Change Order',
        message: `Approve change order ${confirmAction.coNumber}? This will mark it as approved and include it in the revised contract value.`,
        confirmLabel: 'Approve',
        variant: 'default' as const,
      };
    } else {
      return {
        title: 'Decline Change Order',
        message: `Decline change order ${confirmAction.coNumber}? This cannot be undone.`,
        confirmLabel: 'Decline',
        variant: 'danger' as const,
      };
    }
  })() : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 text-[22px] font-bold leading-tight tracking-[-0.015em]">
          Change Orders
        </h2>
        {!showNewForm && editingId === null && (
          <button
            onClick={() => setShowNewForm(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined text-base">add</span>
            New Change Order
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {showNewForm && (
        <COForm
          jobId={numericJobId}
          onSaved={handleSaved}
          onCancel={() => setShowNewForm(false)}
          onFormError={msg => toast.error(msg)}
        />
      )}

      {loading ? (
        <div className="py-12 text-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
          <p className="text-sm text-gray-400">Loading…</p>
        </div>
      ) : orders.length === 0 && !showNewForm ? (
        <div className="py-16 bg-white rounded-xl border border-gray-200 text-center">
          <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">change_circle</span>
          <p className="text-sm text-gray-500 mb-4">No change orders yet.</p>
          <button
            onClick={() => setShowNewForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined text-base">add</span>
            Create First Change Order
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(co => (
            <div key={co.id}>
              {editingId === co.id ? (
                <COForm
                  initial={co}
                  jobId={numericJobId}
                  onSaved={handleSaved}
                  onCancel={() => setEditingId(null)}
                  onFormError={msg => toast.error(msg)}
                />
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-gray-900">{co.change_order_number}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${statusBadge[co.status]}`}>
                        {co.status}
                      </span>
                    </div>
                    <p className="text-base font-bold text-gray-900">{money(co.total)}</p>
                  </div>

                  <p className="text-sm font-semibold text-gray-800 mt-1">{co.title}</p>
                  {co.description && (
                    <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{co.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{fmtDate(co.created_at)}</p>

                  {/* Approve URL block (sent/approved/declined or after send) */}
                  {(co.status !== 'draft' || sentUrl?.id === co.id) && (
                    <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1.5">Approve Link</p>
                      {co.approval_token || sentUrl?.id === co.id ? (
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs bg-white border border-gray-200 rounded px-2 py-1 text-gray-700 truncate font-mono">
                            {sentUrl?.id === co.id
                              ? sentUrl.url
                              : `${window.location.origin}/approve/change-order/${co.approval_token}`}
                          </code>
                          <button
                            onClick={() => copyUrl(
                              sentUrl?.id === co.id
                                ? sentUrl.url
                                : `${window.location.origin}/approve/change-order/${co.approval_token}`,
                              co.id
                            )}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-xs font-medium text-gray-600 hover:border-primary hover:text-primary transition-colors"
                          >
                            <span className="material-symbols-outlined text-base leading-none">
                              {copiedId === co.id ? 'check' : 'content_copy'}
                            </span>
                            {copiedId === co.id ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">No approval token available.</p>
                      )}
                      {co.approved_at && (
                        <p className="text-xs text-green-700 mt-1.5 font-medium">
                          Approved {co.approved_by_name ? `by ${co.approved_by_name}` : ''} on {fmtDate(co.approved_at)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {co.status === 'draft' && (
                      <>
                        <button
                          onClick={() => setEditingId(co.id)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <span className="material-symbols-outlined text-base leading-none">edit</span>
                          Edit
                        </button>
                        <button
                          onClick={() => handleSend(co.id)}
                          disabled={sendingId === co.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
                        >
                          <span className="material-symbols-outlined text-base leading-none">
                            {sendingId === co.id ? 'progress_activity' : 'send'}
                          </span>
                          {sendingId === co.id ? 'Sending…' : 'Send'}
                        </button>
                        <button
                          onClick={() => setConfirmAction({ type: 'delete', id: co.id })}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <span className="material-symbols-outlined text-base leading-none">delete</span>
                          Delete
                        </button>
                      </>
                    )}

                    {/* Approve / Decline available for draft and sent */}
                    {(co.status === 'draft' || co.status === 'sent') && (
                      <>
                        <button
                          onClick={() => setConfirmAction({ type: 'approve', id: co.id, coNumber: co.change_order_number })}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors"
                        >
                          <span className="material-symbols-outlined text-base leading-none">check_circle</span>
                          Approve
                        </button>
                        <button
                          onClick={() => setConfirmAction({ type: 'decline', id: co.id, coNumber: co.change_order_number })}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <span className="material-symbols-outlined text-base leading-none">cancel</span>
                          Decline
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Confirm dialog (delete / approve / decline) */}
      {confirmAction && confirmProps && (
        <ConfirmDialog
          title={confirmProps.title}
          message={confirmProps.message}
          confirmLabel={actionInProgress ? `${confirmProps.confirmLabel}…` : confirmProps.confirmLabel}
          variant={confirmProps.variant}
          onConfirm={handleConfirm}
          onCancel={() => { if (!actionInProgress) setConfirmAction(null); }}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};
